import { type ReactNode, useEffect } from "react";
import { PixelRatio, Pressable, Text, View } from "react-native";
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

import { CardArt } from "@/components/ui/CardArt";
import { Checkbox } from "@/components/ui/Checkbox";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import {
  cardNameWithoutNumber,
  formatCardIdentity,
  type GradeSeal,
} from "@/lib/catalog/cardIdentity";
import { cn } from "@/lib/ui/cn";
import type { Rarity } from "./RarityBadge";
import { slabCardWindow, slabDensity } from "./slab";
import { SlabFrame } from "./SlabFrame";

/** Shared inventory, catalog, and card-detail tile presentations. */
export type CardTileSize = "tile" | "hero" | "catalog" | "pocket";

export type CardTileProps = {
  name: string;
  set?: string;
  number?: string;
  /** Short printing or finish tag (#540), such as "Reverse Holo" or "1st Edition", appended to the
   *  `set · #number` caption so two units of the same printing family read apart. */
  variant?: string;
  /** The card's rarity tier, drawn as the art border's color. Omitted means unknown, not common
   *  (#413): the tile draws a neutral border rather than coloring a unit with no rarity data (a
   *  generic unit, a catalog miss, a sealed box) as the quietest tier. */
  rarity?: Rarity;
  /** Provider rarity shown beside the collector number in a catalog tile. */
  rarityLabel?: string;
  /** The slab this unit is in (`formatGradeSeal`). Present means the art is drawn inside that
   *  grader's holder (TRO-627); absent is a raw copy. Below the frame's smallest legible size the
   *  tile shows a text grade chip instead (see `slabDensity`). */
  seal?: GradeSeal;
  image?: string;
  /** Draws the holo sheen across the art; the caller decides which printings shimmer. */
  foil?: boolean;
  /** Draws the selected outline, for a tile picked in a multi-select. */
  selected?: boolean;
  onPress?: () => void;
  /** Responsive cell width (#413), for grids that size cells to their column count. Omitted keeps
   *  the size's fixed brand width. */
  width?: number;
  /** Which tile presentation to render. `"tile"` (the default) is the grid cell, at the
   *  180px standalone brand width or the `width` a grid passes. `"hero"` is the card-detail
   *  flagship: a wider tile with larger type and the name announced as the page's heading.
   *  "pocket" fits Binder pages: edge-to-edge 63:88 art and a single 12 pt name line. */
  size?: CardTileSize;
  /** How many lines a grid cell's name may take. `1` (the default) keeps a wall of cells on one
   *  rhythm; the public storefront passes `2` because its cells are about 114pt wide, where one
   *  line fits about 13 characters. Ignored by `size="hero"`, which always takes two. */
  nameLines?: number;
  /** Keeps the footer aligned with neighboring grid cells that have a set or collector number. */
  reserveIdentityLine?: boolean;
  /** Fires when the tile is held, such as to enter select mode. */
  onLongPress?: () => void;
  /** A per-screen footer below the name and caption, such as a grid's price lines (#413). */
  footer?: ReactNode;
  /** A small overlay pinned to the art's top-right, such as a ×N quantity badge (#413). */
  badge?: ReactNode;
  /** Select mode: `true` or `false` overlays a leading checkbox on the art in place of the grade
   *  seal; `undefined` (the default) renders the plain tile. */
  checkboxOn?: boolean;
  /** Screen-reader label for the whole tile; falls back to the rendered name and caption. */
  accessibilityLabel?: string;
  /** Where the card's name and `set · #number` caption go (TRO-775). `"inline"` (the default) is
   *  the tile's own block beneath the art. `"hidden"` draws the art alone, for a layout that puts
   *  the art beside something else (the card-detail hero's three-source price stack) and prints
   *  the identity below the pair. The caller then renders it through `CardHeroCaption`, so the
   *  format has one owner and cannot print twice. */
  identityPlacement?: "inline" | "hidden";
  /** testIDs for the tile and its two text lines, so a surface with an e2e `testID` contract (the
   *  public browse grid's `browse-item-*`) keeps it on the shared tile. Omitted leaves every node
   *  untagged. */
  testIDs?: { tile?: string; name?: string; identity?: string; };
};

const RARITY_BORDER: Record<Rarity, string> = {
  common: "border-rarity-common",
  rare: "border-rarity-rare",
  epic: "border-rarity-epic",
  legendary: "border-rarity-legendary",
};

/** The inset from the tile's edge to its art box: the `border` hairline plus the `p-2.5` gutter.
 *  React Native is border-box, so both come out of the tile's declared width. */
const ART_INSET = 1 + 10;
/** The art box's aspect as height ÷ width, matching the art View's `aspect-[3/4]`. */
const ART_ASPECT = 4 / 3;
/** The art box's rarity border (`border-2`), which `cardTileArtBox` includes and anything laid out
 *  *inside* that box has to take back off. */
const ART_BORDER = 2;
/** The standalone tile's fixed width (`w-[180px]`), used when no responsive `width` is given. */
const TILE_WIDTH = 180;
/** The hero's fixed width (#760), a third wider than the grid cell so the art reads as the page's
 *  subject. It fits inside the detail page's `px-4` gutters on the narrowest supported phone
 *  (320pt), so the hero needs no responsive measure. */
const HERO_WIDTH = 240;

/**
 * Returns where the art box sits inside a tile of `width`, in px from the tile's top-left.
 *
 * The inset and the 3:4 aspect are the tile's private layout, so a surface that pins an overlay to
 * the art reads it from here instead of re-deriving it. The public browse grid's pick affordance
 * (#413) sits over the art's bottom-right corner but must be a sibling of the tile Pressable, since
 * a nested Pressable doubles the tap and screen-reader target (#469), so it is positioned
 * absolutely.
 */
export function cardTileArtBox(
  width: number = TILE_WIDTH,
): { top: number; left: number; width: number; height: number; } {
  const artWidth = width - 2 * ART_INSET;
  return { top: ART_INSET, left: ART_INSET, width: artWidth, height: artWidth * ART_ASPECT };
}

const SHEEN_WIDTH = 60;
/** How far the sheen overhangs the art box top and bottom, so a tilted bar still covers the
 *  corners it would otherwise cut. */
const SHEEN_OVERHANG = 20;
/** The sheen's tilt. A diagonal reads as a light source raking across the print; upright would
 *  read as a UI wipe. */
const SHEEN_ROTATION_DEG = 18;

/**
 * Returns the sheen bar's start and end `translateX` across an art box of `artWidth`.
 *
 * The bar is rotated and taller than the art, so its extent on the art's x-axis is its rotated
 * footprint, not its 60px width (about 80px per side on the hero). The travel must clear that
 * footprint at both ends, or a translucent sliver stays visible when the loop snaps back.
 */
export function cardTileSheenTravel(artWidth: number): { from: number; to: number; } {
  const rad = (SHEEN_ROTATION_DEG * Math.PI) / 180;
  const height = artWidth * ART_ASPECT + 2 * SHEEN_OVERHANG;
  const footprint = SHEEN_WIDTH * Math.cos(rad) + height * Math.sin(rad);
  const clearance = footprint / 2 + SHEEN_WIDTH / 2;
  return { from: -clearance, to: artWidth + footprint / 2 - SHEEN_WIDTH / 2 };
}

/** Opt-in holo sheen: a translucent diagonal bar sweeping across the art, static under reduced
 *  motion. The sweep is measured from the art box's `artWidth` rather than a constant, so it covers
 *  a hero's wide art and a narrow grid cell's alike (#760). */
function FoilShimmer({ artWidth }: { artWidth: number; }) {
  const reduced = useReducedMotion();
  const x = useSharedValue(0);
  useEffect(() => {
    if (!reduced) {
      x.value = withRepeat(withTiming(1, { duration: 2600, easing: Easing.linear }), -1, false);
    } else {
      cancelAnimation(x);
      x.value = 0;
    }
    return () => cancelAnimation(x);
  }, [reduced, x]);
  // Under reduced motion there is no sweep, so the bar rests centred on the art.
  const restX = (artWidth - SHEEN_WIDTH) / 2;
  const { from, to } = cardTileSheenTravel(artWidth);
  const style = useAnimatedStyle(() => ({
    opacity: reduced ? 0.18 : 0.35,
    transform: [
      { translateX: reduced ? restX : from + x.value * (to - from) },
      { rotateZ: `${SHEEN_ROTATION_DEG}deg` },
    ],
  }));
  return (
    <Animated.View
      // Named, because it is no longer the only node here that takes no touches: since TRO-1234
      // the holder's own gloss does too, and a test picking the first `pointerEvents="none"` node
      // would read the case's highlight and call it the sheen.
      testID="card-tile-foil"
      pointerEvents="none"
      style={[
        {
          position: "absolute",
          top: -SHEEN_OVERHANG,
          bottom: -SHEEN_OVERHANG,
          left: 0,
          width: SHEEN_WIDTH,
          backgroundColor: "rgba(255,255,255,0.55)",
        },
        style,
      ]}
    />
  );
}

/**
 * A flat collectible-card tile: rarity-colored art border, an optional grade seal in tabular
 * type, and the name in the Title face. The tier is never named (TRO-775): a dealer prices a card
 * by what it sells for, so the tier shows only as the border's color (a caller turns on `foil`
 * for a chase tier, `isChaseRarity`).
 *
 * It has no resting or hover drop-shadow (the No-Shadow Rule); the affordance is the hairline
 * border plus `active:scale`. Foil shimmer is opt-in (`foil`) and honors `prefers-reduced-motion`
 * via `useReducedMotion`.
 *
 * Every collectible-card grid in the app renders through this tile (#413): the inventory stock
 * grid, the scan set-browse grid and the public browse storefront. Each supplies its own money and
 * affordances through the `footer` and `badge` slots rather than forking the tile.
 *
 * `size="hero"` is the same tile at card-detail scale (#760), not a detail-page fork: one rarity
 * ramp, one grade seal and one `formatCardIdentity` caption, so a card the dealer saw in a grid is
 * recognisably the same object on its own page.
 */
export function CardTile(
  {
    name,
    set,
    number,
    variant,
    rarity,
    rarityLabel,
    seal,
    image,
    foil = false,
    selected = false,
    onPress,
    onLongPress,
    width,
    size = "tile",
    nameLines = 1,
    reserveIdentityLine = false,
    footer,
    badge,
    checkboxOn,
    accessibilityLabel,
    identityPlacement = "inline",
    testIDs,
  }: CardTileProps,
) {
  const hero = size === "hero";
  const catalog = size === "catalog";
  const pocket = size === "pocket";
  // A responsive `width` (a grid cell) wins over the size's fixed brand width; the plain tile has
  // neither and keeps its `w-[180px]` class instead of an inline measure.
  const measuredWidth = width ?? (hero ? HERO_WIDTH : null);
  // The same caption the browse list row and detail page use, so one card reads identically
  // everywhere (#530/#540): "Fossil · #5/62 · Reverse Holo", or nothing for a generic or
  // uncatalogued unit rather than a lone separator.
  const identity = [formatCardIdentity(set, number, variant), catalog ? rarityLabel : undefined]
    .filter(Boolean).join(" · ");
  const title = catalog ? cardNameWithoutNumber(name, number) : name;
  const selecting = checkboxOn !== undefined;
  // A tile with nothing to press is not a Pressable. The hero is the one such call site, and a
  // Pressable there would scale on a tap that does nothing and, as an accessibility element by
  // default, would flatten the tile's children into one announcement and hide the heading role on
  // the hero's name (#531/#760).
  const interactive = !!(onPress || onLongPress || selecting || selected);
  const Frame = interactive ? Pressable : View;
  // The slab reads the same published geometry as overlays do, so a gutter change moves both.
  const baseArtBox = cardTileArtBox(measuredWidth ?? TILE_WIDTH);
  const artBox = pocket
    ? {
      top: 1,
      left: 1,
      width: (measuredWidth ?? TILE_WIDTH) - 2,
      height: ((measuredWidth ?? TILE_WIDTH) - 2) * 88 / 63,
    }
    : catalog
    ? { ...baseArtBox, height: baseArtBox.width * 7 / 5 }
    : baseArtBox;
  // A slab is drawn only where it is legible; below that the tile shows the text grade chip
  // (`slabDensity`).
  // The holder is laid out in the art box's *content* box, inside the 2px rarity border, so it is
  // measured there too. Handing it the border box made it 4px too tall: it hung past the bottom
  // border, and only the clip below hid that.
  const slabBox = { width: artBox.width - 2 * ART_BORDER, height: artBox.height - 2 * ART_BORDER };
  // A slab is drawn only where it is legible; below that the tile shows the text grade chip
  // (`slabDensity`).
  const density = seal ? slabDensity(slabBox.width) : "seal";
  const slabbed = !!seal && density !== "seal";
  const cardWindow = slabCardWindow(slabBox.width, slabBox.height, density);
  // Hoisted so the slabbed and raw branches render the same art node, including the no-image
  // fallback.
  const artFallback = (
    <View className="h-full w-full items-center justify-center p-2" aria-hidden>
      <Text className="text-center font-body text-[11px] text-muted-foreground">
        {catalog ? "Image unavailable" : name}
      </Text>
    </View>
  );
  const art = image
    ? <CardArt uri={image} contentFit={catalog ? "contain" : "cover"} fallback={artFallback} />
    : artFallback;
  return (
    <Frame
      testID={testIDs?.tile}
      onPress={onPress}
      onLongPress={onLongPress}
      accessibilityLabel={accessibilityLabel}
      // An unlabelled non-interactive tile stays transparent so its name and caption reach
      // the screen reader individually. A labelled one must opt in, because a `View` is not an
      // accessibility element by default and its label would never be announced.
      accessible={interactive ? undefined : !!accessibilityLabel}
      // A checkbox in select mode; otherwise a button whenever the tile is tappable or selected, so
      // a navigating grid cell announces as actionable on every platform and `selected` always has
      // a role to carry it. A labelled tile with nothing to press (the read-only public inventory
      // grid, ADR-0056) takes "text", which carries the name without promising a tap. A decorative
      // tile (no press, no selection, no label) has no role.
      accessibilityRole={selecting
        ? "checkbox"
        : onPress || selected
        ? "button"
        : accessibilityLabel
        ? "text"
        : undefined}
      accessibilityState={selecting
        ? { checked: checkboxOn ?? false }
        : selected
        ? { selected: true }
        : undefined}
      // react-native-web ignores `accessibilityState`, so without this the web checkbox has no
      // `aria-checked` and announces no state. Native reads the same value either way.
      aria-checked={selecting ? checkboxOn : undefined}
      style={measuredWidth != null ? { width: measuredWidth } : undefined}
      className={cn(
        // Ordinary tiles share the art-box gutter; Binder sleeves let the art fill their width.
        "rounded-xl border border-border bg-surface",
        pocket ? "p-0 overflow-hidden" : "p-2.5",
        catalog && "overflow-hidden",
        interactive && "active:scale-[.98]",
        hero ? "gap-3" : catalog || pocket ? "gap-0" : "gap-2.5",
        measuredWidth == null && "w-[180px]",
        selected && "border-primary",
      )}
    >
      <View
        style={pocket ? { aspectRatio: 63 / 88 } : undefined}
        className={cn(
          pocket ? "rounded-lg border-2 bg-surface-muted" : catalog
            ? "aspect-[5/7] rounded-lg bg-surface-muted"
            : "aspect-[3/4] rounded-lg border-2 bg-surface-muted",
          // Raw art is drawn edge to edge and the foil sheen deliberately overhangs the box, so both
          // need this clip. A holder does not: it clips its own window, and the clip fell exactly
          // where its cast shadow lands, cutting off the one part of ADR-0144's depth that says the
          // thing is an object. The shadow reaches into the tile's own 10px gutter instead.
          !slabbed && "overflow-hidden",
          // Unknown rarity keeps the border weight but drops the tier color, so it reads as
          // structure rather than as "this card is common".
          rarity ? RARITY_BORDER[rarity] : "border-border",
        )}
      >
        {slabbed
          ? (
            <SlabFrame
              grader={seal?.company}
              grade={seal?.grade}
              width={slabBox.width}
              height={slabBox.height}
            >
              {art}
              {/* Inside the window, so the sheen crosses the card and not the slab's case or label. */}
              {foil ? <FoilShimmer artWidth={cardWindow.width} /> : null}
            </SlabFrame>
          )
          : (
            <>
              {art}
              {foil ? <FoilShimmer artWidth={artBox.width} /> : null}
            </>
          )}
        {
          /* Top-left: the select-mode checkbox covers part of the slab's label plate. Select mode
            is transient, the checkbox belongs where the eye looks for it, and the grade stays
            legible at the plate's other end. */
        }
        {selecting
          ? (
            <View className="absolute left-2 top-2">
              <Checkbox on={checkboxOn ?? false} overlay />
            </View>
          )
          : seal && !slabbed
          ? (
            <View
              className={cn(
                "absolute left-2 top-2 rounded-md bg-foreground",
                hero ? "px-2 py-1.5" : "px-1.5 py-1",
              )}
            >
              <Text
                className={cn(
                  "font-num-bold text-bg",
                  hero ? "text-[15px]" : "text-[12px]",
                )}
              >
                {seal.label}
              </Text>
            </View>
          )
          : null}
        {/* Top-right: a per-screen badge (e.g. ×N quantity). */}
        {badge ? <View className="absolute right-2 top-2">{badge}</View> : null}
      </View>
      <View className={catalog ? "py-2" : undefined}>
        {identityPlacement === "hidden" ? null : (
          <Text
            testID={testIDs?.name}
            // A hero is the page's subject, so its name is the page heading (#531/#760). A grid cell
            // is one of many and heads nothing.
            accessibilityRole={hero ? "header" : undefined}
            // The hero shows two lines so a detail page says the card's whole name. A grid cell
            // truncates to `nameLines` to keep the wall on one rhythm.
            numberOfLines={hero ? 2 : nameLines}
            style={pocket ? { lineHeight: 18, paddingHorizontal: 4 } : catalog
              ? { minHeight: 18 * nameLines * PixelRatio.getFontScale(), lineHeight: 18 }
              : undefined}
            className={cn(
              "font-title text-foreground",
              hero
                ? "text-[20px]"
                : pocket
                ? "text-[12px]"
                : catalog
                ? "text-[14px]"
                : "text-[15px]",
            )}
          >
            {title}
          </Text>
        )}
        {/* Omitted, not left empty, when there is no caption, so no row spends vertical rhythm. */}
        {identityPlacement === "hidden" || !identity ? null : (
          <Text
            testID={testIDs?.identity}
            numberOfLines={catalog ? 2 : 1}
            className={cn(
              "mt-1 font-body text-muted-foreground",
              hero ? "text-[13px]" : "text-[12px]",
            )}
          >
            {identity}
          </Text>
        )}
        {reserveIdentityLine && !identity && !hero && !catalog ? <View className="h-5" /> : null}
        {footer}
      </View>
    </Frame>
  );
}
